import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@mantine/core';
import { useCopy } from '@/lib/settings';
import { StepHeader } from '@/components/ui/StepHeader';
import { StickyActionBar } from '@/components/ui/StickyActionBar';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Callout } from '@/components/ui/Callout';
import { EventPicker } from '@/components/rides/EventPicker';
import { useDriverReadiness } from '@/components/flows/DriverReadiness';
import { useOfferFlow } from './_shared';

/**
 * Offer a lift, step 1: the services and events (tick as many as you like). If something still
 * stops riders from seeing the driver's lifts, it says so here, before the driver sets anything up.
 */
export default function DriveEventsPage() {
  const navigate = useNavigate();
  const { t } = useCopy();
  const f = useOfferFlow();
  const { events, driver, setDriver } = f;
  const readiness = useDriverReadiness();
  const [tried, setTried] = useState(false);
  const n = driver.eventIds.length;

  const toggle = (id: number) => {
    setTried(false);
    setDriver({ eventIds: driver.eventIds.includes(id) ? driver.eventIds.filter((x) => x !== id) : [...driver.eventIds, id] });
  };
  const onContinue = () => {
    setTried(true);
    if (n) navigate('/drive/route');
  };
  const todo = readiness.todos.length;

  return (
    <div className="rides-main rides-flow">
      <StepHeader step={1} total={f.total} title="Which services are you driving to?" lede={t('drive.events_intro')} backTo="/drive/profile" />
      <div className="rides-stepbody">
      <div className="rides-flow-stack">
        {!readiness.loading && readiness.overall !== 'ready' && (
          <Callout
            tone={readiness.overall === 'waiting' ? 'info' : 'warn'}
            title={readiness.isNew ? 'First, get ready to drive' : readiness.overall === 'waiting' ? 'Your checks are not finished yet' : 'Your checklist is not finished yet'}
            action={<Button variant="default" component={Link} to="/drive/profile">{readiness.isNew ? 'Get started' : 'See your checklist'}</Button>}
          >
            {readiness.isNew
              ? t('drive.ready_intro')
              : readiness.overall === 'waiting'
                ? t('drive.events_waiting')
                : t(todo === 1 ? 'drive.events_todo_one' : 'drive.events_todo_many', undefined, { count: todo })}
          </Callout>
        )}
        {events.isLoading && <LoadingState label="Loading services and events" count={3} />}
        {events.error && <ErrorState title="We couldn't load the services" error={events.error} onRetry={() => events.refetch()} retrying={events.isFetching} />}
        {events.data && (
          <EventPicker
            events={events.data}
            values={driver.eventIds}
            onToggle={toggle}
            emptyAction={<Button component={Link} to="/drive/listings" variant="default" fullWidth>See my lifts</Button>}
          />
        )}
        {tried && !n && <Callout tone="bad" live>Please choose at least one service.</Callout>}
      </div>
      </div>
      {events.data && events.data.length > 0 && (
        <StickyActionBar hint={n ? (n === 1 ? '1 service chosen' : `${n} services chosen`) : undefined}>
          <Button fullWidth onClick={onContinue}>Continue</Button>
        </StickyActionBar>
      )}
    </div>
  );
}
